import { useState } from 'react';
import axios from 'axios';
import { mobileLogout } from '../utils/axiosConfig';

/**
 * Custom hook for handling logout functionality
 * Eliminates duplicated logout logic across 10+ components
 *
 * @param {Function} onLogout - Callback function called on successful logout
 * @returns {Object} - { handleLogout, isLoggingOut, logoutError }
 */
const useLogout = (onLogout) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState(null);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    setLogoutError(null);

    // Issue #435: Logout must ALWAYS succeed from the user's point of view.
    // Whether the server request succeeds, returns 401 (session already
    // expired) or fails entirely (network/server error), we still clear the
    // local session and navigate to the login screen. Previously a non-401
    // failure left the user stranded on an authenticated page with a dead
    // session and a "Beim Abmelden ..." error banner, so logout appeared broken.
    try {
      // Best-effort server-side logout (invalidates session, cleans up push subs)
      await axios.post('/api/logout');
    } catch (err) {
      // Already-expired sessions (401) and network/server errors are both
      // non-fatal here: the local logout below still proceeds.
      console.error('Logout request failed (continuing with local logout):', err);
    } finally {
      // Clear mobile session token storage
      await mobileLogout();
      // Issue #327: Clear dismissed announcements so new session starts fresh
      localStorage.removeItem('dismissedAnnouncements');
      setIsLoggingOut(false);
      // Hand control back to the app to reset state and show the login screen
      onLogout?.();
    }
  };

  const clearLogoutError = () => {
    setLogoutError(null);
  };

  return {
    handleLogout,
    isLoggingOut,
    logoutError,
    clearLogoutError
  };
};

export default useLogout;
